import { Check, RefreshCw, Share2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { cn } from "../lib/utils";
import { MEDOPILOT_SW_UPDATE_EVENT, type ServiceWorkerUpdateEvent } from "./registerServiceWorker";
import { usePwaInstallState } from "./usePwaInstallState";

// Shared "premium" CTA look for this card: a solid white pill with brand-blue text, matching
// the marketing "Get started" CTA already used on the cta-gradient-premium hero section.
const ctaButtonClassName =
  "inline-flex min-h-10 items-center justify-center rounded-full bg-white px-4 text-sm font-semibold text-[#084aa8] shadow-[0_14px_30px_-16px_rgba(0,0,0,0.55)] transition-all hover:-translate-y-0.5 hover:bg-[#f7fbff] hover:shadow-[0_18px_36px_-18px_rgba(0,0,0,0.6)] focus:outline-none focus:ring-4 focus:ring-white/40";

const installBenefits = [
  "Quick access to your health information",
  "Ready for future reminders",
  "A seamless experience across devices",
];

export function PwaStatusPrompts() {
  const { canPromptInstall, canShowIosGuidance, cardDismissed, requestInstall, dismiss, openIosInstructions } =
    usePwaInstallState();
  const [applyUpdate, setApplyUpdate] = useState<(() => void) | null>(null);
  // The automatic install invitation belongs on the public landing/home page only -- it must not
  // follow the user around, least of all into the authenticated app. The explicit "Install
  // MedoPilot" nav/footer entry points (InstallMedoPilotEntry) are unaffected: they render wherever
  // that marketing chrome does and are user-initiated, not automatic. The update-ready prompt is a
  // different concern and stays available on every route.
  const onHomePage = useLocation().pathname === "/";

  useEffect(() => {
    const handleUpdateAvailable = (event: ServiceWorkerUpdateEvent) => {
      setApplyUpdate(() => event.detail.applyUpdate);
    };

    window.addEventListener(MEDOPILOT_SW_UPDATE_EVENT, handleUpdateAvailable as EventListener);
    return () => window.removeEventListener(MEDOPILOT_SW_UPDATE_EVENT, handleUpdateAvailable as EventListener);
  }, []);

  const showUpdate = Boolean(applyUpdate);
  // Update-ready always wins if it and install guidance are both pending, so the card never
  // shows two competing calls to action at once.
  const showInstall = !showUpdate && onHomePage && canPromptInstall && !cardDismissed;
  const showIosGuidance = !showUpdate && onHomePage && canShowIosGuidance && !cardDismissed;

  if (!showInstall && !showUpdate && !showIosGuidance) {
    return null;
  }

  const title = showUpdate ? "A MedoPilot update is ready" : "Install MedoPilot";
  const body = showUpdate
    ? "Apply it when you are ready to refresh this session."
    : showIosGuidance
      ? "Tap Share, then Add to Home Screen for quick access anytime."
      : "Keep MedoPilot close at hand, with quick access from your device.";

  return (
    <aside
      className={cn(
        "fixed inset-x-4 bottom-[calc(5.75rem+env(safe-area-inset-bottom))] z-50 mx-auto max-w-md overflow-hidden rounded-3xl p-4 text-white shadow-[0_32px_80px_-28px_rgba(2,10,32,0.85)] ring-1 ring-white/12 sm:max-w-xl sm:p-5",
        "lg:bottom-5 lg:right-5 lg:left-auto lg:mx-0 lg:max-w-sm",
      )}
      aria-live="polite"
      aria-label={title}
    >
      {/* Base: MedoPilot's existing premium dark navy/indigo/blue gradient, unchanged. */}
      <div className="cta-gradient-premium absolute inset-0" aria-hidden="true" />
      {/* Very restrained medical-tech texture: a faint CSS-only dot grid, no new image asset. */}
      <div
        className="absolute inset-0 opacity-[0.14] [background-image:radial-gradient(circle,rgba(255,255,255,0.9)_1px,transparent_1px)] [background-size:18px_18px]"
        aria-hidden="true"
      />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white/25" aria-hidden="true" />
      <div className="pointer-events-none absolute -right-10 -top-12 size-44 rounded-full bg-[#5c8dff]/30 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-14 -left-10 size-40 rounded-full bg-[#010d23]/60 blur-3xl" aria-hidden="true" />
      <div className="relative flex items-start gap-3">
        <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-white/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_12px_26px_-10px_rgba(0,0,0,0.6)] ring-1 ring-white/40">
          {showUpdate ? (
            <RefreshCw className="size-5 text-primary" aria-hidden="true" />
          ) : showIosGuidance ? (
            <Share2 className="size-5 text-primary" aria-hidden="true" />
          ) : (
            <img src="/medopilot-icon.png" alt="" className="size-7" width={28} height={28} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-base font-bold leading-tight text-white">{title}</p>
          <p className="mt-1.5 text-sm font-medium leading-5 text-white/80">{body}</p>
          {showInstall || showIosGuidance ? (
            <ul className="mt-2.5 space-y-1">
              {installBenefits.map((benefit) => (
                <li key={benefit} className="flex items-start gap-1.5 text-xs font-medium leading-4 text-white/85">
                  <Check className="mt-0.5 size-3.5 shrink-0 text-[#7fb0ff]" aria-hidden="true" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="mt-3.5 flex flex-wrap gap-2">
            {showUpdate && applyUpdate ? (
              <button type="button" onClick={applyUpdate} className={ctaButtonClassName}>
                Update
              </button>
            ) : null}
            {showInstall ? (
              <button
                type="button"
                onClick={() => {
                  // Only invoked from this explicit click -- the module-level capture never calls
                  // prompt() itself, it only suppresses the browser's own default UI.
                  void requestInstall();
                }}
                className={ctaButtonClassName}
              >
                Install
              </button>
            ) : null}
            {showIosGuidance ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    // Hand off to the detailed step-by-step panel (IosInstallInstructions, shared
                    // showIosInstructions flag). Dismiss this card so the two don't stack -- the
                    // panel is now the active guidance. No install API is invoked; this is purely
                    // instructional, and Chromium's beforeinstallprompt path is untouched.
                    openIosInstructions();
                    dismiss();
                  }}
                  className={ctaButtonClassName}
                >
                  Show me how
                </button>
                <button
                  type="button"
                  onClick={dismiss}
                  className="inline-flex min-h-10 items-center justify-center rounded-full border border-white/40 px-4 text-sm font-semibold text-white transition-colors hover:bg-white/10 focus:outline-none focus:ring-4 focus:ring-white/40"
                >
                  Got it
                </button>
              </>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            // Dismissing (without installing) only hides the card for this render -- the
            // captured event itself is left intact, so it is still available if the user reopens
            // or otherwise re-triggers the card before the next page load.
            dismiss();
            setApplyUpdate(null);
          }}
          className="grid size-8 shrink-0 place-items-center rounded-full text-white/70 transition hover:bg-white/12 hover:text-white focus:outline-none focus:ring-4 focus:ring-white/40"
          aria-label={`Dismiss ${title}`}
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </aside>
  );
}
